<template>
  <Dialog
    :model-value="visible"
    :closable="false"
    @update:model-value="onUpdateVisible"
  >
    <div class="act-time-dialog">
      <div class="main-text">
        {{
          props.type === 'ended'
            ? t('halloween.actTime.ended')
            : t('halloween.actTime.notStart')
        }}
      </div>
      <div v-if="props.type === 'not-start'" class="countdown-block">
        <div class="countdown-label">[{{ t('halloween.actTime.startCountdown') }}]</div>
        <div class="countdown-value">
          {{ startCountdown.day }} {{ t('halloween.actTime.day') }}
          {{ startCountdown.hour }}:{{ startCountdown.minute }}:{{ startCountdown.second }}
        </div>
      </div>
    </div>
  </Dialog>
</template>

<script setup>
import {
  computed,
  defineEmits,
  defineProps,
  onBeforeUnmount,
  ref,
  watch,
} from 'vue'
import { useI18n } from 'vue-i18n'
import Dialog from '../index.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  // not-start | ended
  type: {
    type: String,
    default: 'not-start',
  },
  // 活动开始时间戳（毫秒）
  startMs: {
    type: Number,
    default: 0,
  },
  // 接口返回时的服务端时间戳（毫秒）
  serverMs: {
    type: Number,
    default: 0,
  },
})

const emit = defineEmits(['update:modelValue', 'countdown-end'])
const { t } = useI18n()

const visible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})

// 用「服务端锚点 + 本地墙钟差」算剩余秒数，避免后台页 setInterval 被节流导致倒计时变慢
const clientAnchorMs = ref(0)
const tick = ref(0)
let timer = null
let endedEmitted = false

const clearTimer = () => {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
}

const syncAnchor = () => {
  clientAnchorMs.value = Date.now()
  tick.value += 1
  endedEmitted = false
}

const remainingSec = computed(() => {
  void tick.value
  if (props.type !== 'not-start' || !visible.value) return -1
  const startMs = Number(props.startMs) || 0
  const serverMs = Number(props.serverMs) || 0
  const anchor = Number(clientAnchorMs.value) || 0
  if (!startMs || !serverMs || !anchor) return -1
  const nowServer = serverMs + (Date.now() - anchor)
  return Math.max(0, Math.floor((startMs - nowServer) / 1000))
})

const pad2 = (num) => (num >= 10 ? String(num) : `0${num}`)

const startCountdown = computed(() => {
  const res = { day: '0', hour: '00', minute: '00', second: '00' }
  const diff = remainingSec.value
  if (diff < 0) return res
  res.day = String(Math.floor(diff / 86400))
  res.hour = pad2(Math.floor((diff % 86400) / 3600))
  res.minute = pad2(Math.floor((diff % 3600) / 60))
  res.second = pad2(diff % 60)
  return res
})

const onUpdateVisible = (val) => {
  // 不可关闭：忽略外部试图关掉（overlay 等）
  if (!val && props.type === 'ended') return
  if (!val && props.type === 'not-start' && remainingSec.value > 0) return
  visible.value = val
}

const onVisibilityChange = () => {
  if (document.visibilityState === 'visible' && visible.value && props.type === 'not-start') {
    tick.value += 1
  }
}

const startTimer = () => {
  clearTimer()
  syncAnchor()
  timer = setInterval(() => {
    tick.value += 1
  }, 1000)
}

watch(
  () => [props.modelValue, props.type, props.startMs, props.serverMs],
  ([val]) => {
    if (val && props.type === 'not-start') {
      startTimer()
    } else {
      clearTimer()
    }
  },
  { immediate: true },
)

watch(
  () => remainingSec.value,
  (diff) => {
    if (
      props.type === 'not-start'
      && props.modelValue
      && diff === 0
      && !endedEmitted
    ) {
      endedEmitted = true
      clearTimer()
      visible.value = false
      emit('countdown-end')
    }
  },
)

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', onVisibilityChange)
}

onBeforeUnmount(() => {
  clearTimer()
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', onVisibilityChange)
  }
})
</script>

<style lang="less" scoped>
.act-time-dialog {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  padding: 80px 48px 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #fff7e8;
}

.main-text {
  font-size: 36px;
  font-weight: bold;
  line-height: 1.5;
}

.countdown-block {
  margin-top: 36px;
  font-size: 30px;
  line-height: 1.6;
}

.countdown-label {
  margin-bottom: 12px;
  opacity: 0.9;
}

.countdown-value {
  font-weight: bold;
  color: #ffd37a;
  font-size: 40px;
}
</style>
